import { Component, OnInit, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Telefono, ProveedorTelefono } from '../../../models/telefono.model';
import { TelefonoService } from '../../../services/telefono.service';
import { ToastComponent } from '../../../components/toast/toast.component';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-modal-editar-telefono',
  imports: [ReactiveFormsModule, ToastComponent],
  templateUrl: './modal-editar-telefono.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './modal-editar-telefono.component.css',
})
export class ModalEditarTelefonoComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly telefonoService = inject(TelefonoService);
  private readonly toastService = inject(ToastService);

  cerrar = output<void>();
  telefono = input.required<Telefono>();
  sucursalId = input.required<number>();
  telefonoEditado = output<Telefono>();

  formTelefono = this.formBuilder.group({
    numero: ['', [Validators.required, Validators.maxLength(20)]],
    meta_phone_number_id: ['', [Validators.maxLength(40)]],
    titulo: ['', [Validators.required, Validators.maxLength(50)]],
    descripcion: ['', [Validators.maxLength(120)]],
    activo: [true],
    proveedor: ['OFICIAL' as ProveedorTelefono, [Validators.required]],
  });

  ngOnInit(): void {
    const telefono = this.telefono();
    this.formTelefono.patchValue({
      numero: telefono.numero,
      meta_phone_number_id: telefono.meta_phone_number_id || '',
      titulo: telefono.titulo,
      descripcion: telefono.descripcion || '',
      activo: telefono.activo,
      proveedor: telefono.proveedor || 'OFICIAL',
    });
  }

  guardarTelefono(): void {
    const datosFormulario = this.buildTelefonoFormValues();
    const telefonoActual = this.telefono();

    const sinCambios = this.sinCambios(telefonoActual, datosFormulario);

    if (sinCambios) {
      this.toastService.show({
        text: 'No hay cambios para guardar',
        classname: 'bg-info text-dark',
        delay: 2500,
      });
      return;
    }

    if (this.formTelefono.invalid) {
      this.formTelefono.markAllAsTouched();
      return;
    }

    if (!datosFormulario.numero || !datosFormulario.titulo) {
      return;
    }

    const payload = this.buildPayloadCambios(telefonoActual, datosFormulario);

    this.telefonoService
      .editar(this.sucursalId(), this.telefono().id, payload)
      .subscribe({
        next: (respuesta) => {
          const telefonoActualizado = (respuesta as Telefono) || {
            ...telefonoActual,
            ...payload,
            descripcion:
              payload.descripcion ?? telefonoActual.descripcion ?? null,
          };

          this.telefonoEditado.emit(telefonoActualizado);
          this.toastService.show({
            text: 'Teléfono actualizado exitosamente',
            classname: 'bg-green-500 text-white',
            delay: 3000,
          });
          this.cerrar.emit();
        },
        error: (err) => {
          console.error('Error al editar el teléfono:', err);
          this.toastService.show({
            text: err.message || 'Error al actualizar el teléfono',
            classname: 'bg-red-500 text-white',
            delay: 5000,
          });
        },
      });
  }

  cerrarModal(): void {
    this.cerrar.emit();
  }

  private buildTelefonoFormValues(): {
    numero: string;
    meta_phone_number_id: string;
    titulo: string;
    descripcion: string;
    activo: boolean;
    proveedor: ProveedorTelefono;
  } {
    const { numero, meta_phone_number_id, titulo, descripcion, activo, proveedor } =
      this.formTelefono.value;
    return {
      numero: numero?.trim() || '',
      meta_phone_number_id: meta_phone_number_id?.trim() || '',
      titulo: titulo?.trim() || '',
      descripcion: descripcion?.trim() || '',
      activo: Boolean(activo),
      proveedor: (proveedor as ProveedorTelefono) || 'OFICIAL',
    };
  }

  private sinCambios(
    telefonoActual: Telefono,
    formValues: {
      numero: string;
      meta_phone_number_id: string;
      titulo: string;
      descripcion: string;
      activo: boolean;
      proveedor: ProveedorTelefono;
    },
  ): boolean {
    return (
      formValues.numero === telefonoActual.numero &&
      formValues.meta_phone_number_id ===
        (telefonoActual.meta_phone_number_id || '') &&
      formValues.titulo === telefonoActual.titulo &&
      formValues.descripcion === (telefonoActual.descripcion || '') &&
      formValues.activo === telefonoActual.activo &&
      formValues.proveedor === (telefonoActual.proveedor || 'OFICIAL')
    );
  }

  private buildPayloadCambios(
    telefonoActual: Telefono,
    formValues: {
      numero: string;
      meta_phone_number_id: string;
      titulo: string;
      descripcion: string;
      activo: boolean;
      proveedor: ProveedorTelefono;
    },
  ): Partial<Telefono> {
    const payload: Partial<Telefono> = {};

    if (formValues.numero !== telefonoActual.numero) {
      payload.numero = formValues.numero;
    }
    if (
      formValues.meta_phone_number_id !==
      (telefonoActual.meta_phone_number_id || '')
    ) {
      payload.meta_phone_number_id =
        formValues.meta_phone_number_id || undefined;
    }
    if (formValues.titulo !== telefonoActual.titulo) {
      payload.titulo = formValues.titulo;
    }
    if (formValues.descripcion !== (telefonoActual.descripcion || '')) {
      payload.descripcion = formValues.descripcion;
    }
    if (formValues.activo !== telefonoActual.activo) {
      payload.activo = formValues.activo;
    }
    if (formValues.proveedor !== (telefonoActual.proveedor || 'OFICIAL')) {
      payload.proveedor = formValues.proveedor;
    }

    return payload;
  }
}
